import { CSS_PREFIX, LAUNCHER_ID, LAUNCHER_LABEL } from "./constants";

const STYLE_ID = `${CSS_PREFIX}-styles`;

/**
 * Section 27: this file owns ONLY the shell (overlay/modal/loading/error/
 * close button) — never the reservation flow itself, which lives entirely
 * inside the iframe. Section 8: every selector is namespaced under
 * CSS_PREFIX and injected as a single <style> tag once, so it can never
 * collide with arbitrary Wix page CSS.
 *
 * Section 8/28: kept deliberately calm for this foundation pass — a
 * single opacity transition on the overlay, no scale/translate choreography
 * yet. `--brothers-reservations-enter-ms` is the one hook a later
 * animation pass should need to touch; the rest of this file (scroll
 * lock, focus trap, loading/error states) is independent of it.
 */
const CSS = `
.${CSS_PREFIX}-overlay {
  position: fixed;
  inset: 0;
  z-index: 2147483000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(1, 61, 50, 0);
  opacity: 0;
  transition: opacity var(--${CSS_PREFIX}-enter-ms, 160ms) ease;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}
.${CSS_PREFIX}-overlay[hidden] { display: none; }
.${CSS_PREFIX}-overlay--open {
  opacity: 1;
  background: rgba(1, 61, 50, 0.55);
}
.${CSS_PREFIX}-modal {
  position: relative;
  background: #ffffff;
  border-radius: 16px;
  width: 100%;
  max-width: 480px;
  height: min(760px, 100%);
  max-height: 100%;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.28);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.${CSS_PREFIX}-close {
  /*
    The iframe content fills the entire modal (section 27 — the shell
    never assumes anything about the app's own theme), including
    PD-002's dark petrol background, so this button needs its own
    OPAQUE background + shadow to read clearly against ANY app theme
    behind it — a translucent/brand-colored button was tested and found
    to become near-invisible against a dark iframe background.
  */
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 1;
  width: 36px;
  height: 36px;
  border-radius: 999px;
  border: none;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  color: #013d32;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.${CSS_PREFIX}-close:hover { background: #f2f2f2; }
.${CSS_PREFIX}-close:focus-visible { outline: 2px solid #013d32; outline-offset: 2px; }
.${CSS_PREFIX}-iframe {
  flex: 1;
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
}
.${CSS_PREFIX}-iframe[hidden] { display: none; }
.${CSS_PREFIX}-status {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 32px;
  text-align: center;
  color: #013d32;
}
.${CSS_PREFIX}-status[hidden] { display: none; }
.${CSS_PREFIX}-spinner {
  width: 32px;
  height: 32px;
  border-radius: 999px;
  border: 3px solid rgba(1, 61, 50, 0.15);
  border-top-color: #013d32;
  animation: ${CSS_PREFIX}-spin 0.8s linear infinite;
}
@keyframes ${CSS_PREFIX}-spin { to { transform: rotate(360deg); } }
.${CSS_PREFIX}-retry {
  border: 1px solid #013d32;
  background: #013d32;
  color: #ffffff;
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 14px;
  cursor: pointer;
}
.${CSS_PREFIX}-retry:focus-visible { outline: 2px solid #013d32; outline-offset: 2px; }

/*
  Self-contained launcher (Owner product correction 2026-09-02). Colours are
  PD-002's own booking palette so the launcher and the modal it opens read as
  one product: BROTHERS brand green with the champagne hairline and cream
  text used throughout the booking flow.

  z-index sits deliberately BELOW the overlay's 2147483000 so the modal can
  never end up behind its own launcher, while still clearing typical Wix
  floating elements.
*/
.${CSS_PREFIX}-launcher {
  position: fixed;
  right: max(20px, env(safe-area-inset-right));
  bottom: max(20px, env(safe-area-inset-bottom));
  z-index: 2147482000;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 14px 22px;
  border-radius: 999px;
  border: 1px solid rgba(212, 175, 116, 0.45);
  background: #013d32;
  color: #f5efe6;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  font-size: 15px;
  font-weight: 600;
  line-height: 1;
  letter-spacing: 0.01em;
  cursor: pointer;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.28);
  transition: transform 140ms ease, background 140ms ease;
  -webkit-tap-highlight-color: transparent;
}
.${CSS_PREFIX}-launcher:hover { background: #0f2b22; transform: translateY(-1px); }
.${CSS_PREFIX}-launcher:active { transform: translateY(0); }
.${CSS_PREFIX}-launcher:focus-visible { outline: 2px solid #d4af74; outline-offset: 3px; }
.${CSS_PREFIX}-launcher[hidden] { display: none; }
.${CSS_PREFIX}-launcher-glyph {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: #d4af74;
  flex: 0 0 auto;
}
@media (max-width: 640px) {
  .${CSS_PREFIX}-launcher {
    right: max(16px, env(safe-area-inset-right));
    bottom: max(16px, env(safe-area-inset-bottom));
    padding: 13px 20px;
    font-size: 15px;
  }
}
@media (prefers-reduced-motion: reduce) {
  .${CSS_PREFIX}-launcher { transition: none; }
  .${CSS_PREFIX}-launcher:hover { transform: none; }
}

@media (max-width: 640px), (max-height: 640px) {
  .${CSS_PREFIX}-overlay { padding: 0; }
  .${CSS_PREFIX}-modal {
    max-width: 100%;
    width: 100%;
    height: 100dvh;
    height: 100vh;
    max-height: 100dvh;
    max-height: 100vh;
    border-radius: 0;
    padding-top: env(safe-area-inset-top);
    padding-bottom: env(safe-area-inset-bottom);
    padding-left: env(safe-area-inset-left);
    padding-right: env(safe-area-inset-right);
  }
  .${CSS_PREFIX}-close {
    top: calc(12px + env(safe-area-inset-top));
    right: calc(12px + env(safe-area-inset-right));
  }
}
`;

function injectStylesOnce(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}

export interface ModalElements {
  overlay: HTMLDivElement;
  modal: HTMLDivElement;
  closeButton: HTMLButtonElement;
  iframe: HTMLIFrameElement;
  loadingStatus: HTMLDivElement;
  errorStatus: HTMLDivElement;
  retryButton: HTMLButtonElement;
}

/** Section 4: the exact DOM structure from the spec — overlay > modal > (close, iframe). Built once, reused across open/close cycles (section 22). */
export function buildModal(labels: { close: string; loading: string; errorTitle: string; retry: string }): ModalElements {
  injectStylesOnce();

  const overlay = document.createElement("div");
  overlay.className = `${CSS_PREFIX}-overlay`;
  overlay.setAttribute("role", "presentation");
  overlay.hidden = true;

  const modal = document.createElement("div");
  modal.className = `${CSS_PREFIX}-modal`;
  modal.setAttribute("role", "dialog");
  modal.setAttribute("aria-modal", "true");
  modal.setAttribute("aria-label", labels.close === "" ? "Reserveren" : labels.close);
  modal.tabIndex = -1;

  const closeButton = document.createElement("button");
  closeButton.type = "button";
  closeButton.className = `${CSS_PREFIX}-close`;
  closeButton.setAttribute("aria-label", labels.close);
  closeButton.textContent = "×";

  const loadingStatus = document.createElement("div");
  loadingStatus.className = `${CSS_PREFIX}-status`;
  const spinner = document.createElement("div");
  spinner.className = `${CSS_PREFIX}-spinner`;
  spinner.setAttribute("aria-hidden", "true");
  const loadingText = document.createElement("p");
  loadingText.textContent = labels.loading;
  loadingStatus.append(spinner, loadingText);
  loadingStatus.setAttribute("role", "status");
  loadingStatus.setAttribute("aria-live", "polite");

  const errorStatus = document.createElement("div");
  errorStatus.className = `${CSS_PREFIX}-status`;
  errorStatus.hidden = true;
  const errorText = document.createElement("p");
  errorText.textContent = labels.errorTitle;
  const retryButton = document.createElement("button");
  retryButton.type = "button";
  retryButton.className = `${CSS_PREFIX}-retry`;
  retryButton.textContent = labels.retry;
  errorStatus.append(errorText, retryButton);
  errorStatus.setAttribute("role", "alert");

  const iframe = document.createElement("iframe");
  iframe.className = `${CSS_PREFIX}-iframe`;
  iframe.hidden = true;
  iframe.title = labels.close === "" ? "Reserveren" : labels.close;
  iframe.setAttribute("allow", "payment");

  modal.append(closeButton, loadingStatus, errorStatus, iframe);
  overlay.append(modal);
  document.body.appendChild(overlay);

  return { overlay, modal, closeButton, iframe, loadingStatus, errorStatus, retryButton };
}

let savedScrollY = 0;
let savedBodyOverflow = "";
let savedBodyPaddingRight = "";
let savedHtmlOverflow = "";

/**
 * Section 9: locks host-page scroll without a layout jump (compensates
 * for the vanished scrollbar). Live-verified browser behavior, not an
 * assumption: setting overflow:hidden on <html> ALONE leaves scrollY
 * untouched, but setting it on BOTH <html> AND <body> together (needed
 * for the lock to actually hold in every browser) resets window.scrollY
 * to 0 immediately — invisible to the guest since the opaque overlay
 * covers the page throughout, but real, and NOT restored merely by
 * clearing the overflow styles again. unlockBodyScroll()'s explicit
 * window.scrollTo(0, savedScrollY) is therefore load-bearing, not
 * defensive.
 */
/**
 * Owner product correction 2026-09-02: the loader injects its OWN launcher,
 * so installing the script is the entire Wix integration.
 *
 * Idempotent by element id rather than by a module-level flag: a Wix page
 * re-render, a second Custom Code block, or a re-init must never leave two
 * launchers on the page. Returns the existing node if one is already there.
 */
export function buildLauncher(onClick: () => void): HTMLButtonElement {
  injectStylesOnce();

  const existing = document.getElementById(LAUNCHER_ID);
  if (existing instanceof HTMLButtonElement) return existing;

  const button = document.createElement("button");
  button.type = "button";
  button.id = LAUNCHER_ID;
  button.className = `${CSS_PREFIX}-launcher`;
  button.setAttribute("aria-haspopup", "dialog");
  button.setAttribute("aria-label", LAUNCHER_LABEL);

  const glyph = document.createElement("span");
  glyph.className = `${CSS_PREFIX}-launcher-glyph`;
  glyph.setAttribute("aria-hidden", "true");

  const label = document.createElement("span");
  label.textContent = LAUNCHER_LABEL;

  button.append(glyph, label);
  button.addEventListener("click", (event) => {
    event.preventDefault();
    onClick();
  });

  return button;
}

/**
 * Appends the launcher to <body>, waiting for it if the script somehow runs
 * before it exists (the documented snippet uses `defer`, but a host could
 * paste it without).
 *
 * Deliberately a plain append with NO MutationObserver watching for
 * removal. Wix swaps content INSIDE its own root container on client-side
 * navigation; this button is a direct child of <body>, outside that
 * container, so it is not in the subtree Wix replaces. An observer was
 * written first and then removed: it was speculative complexity for a
 * problem not shown to exist, and it leaked (nothing ever disconnected it).
 * Survival across Wix navigation is verified live on the real site instead
 * — if that ever proves false, add the observer back WITH a disposer.
 */
export function mountLauncher(button: HTMLButtonElement): void {
  const attach = (): void => {
    if (!document.body) return;
    if (!button.isConnected) document.body.appendChild(button);
  };

  if (document.body) attach();
  else document.addEventListener("DOMContentLoaded", attach, { once: true });
}

/** Hidden while the modal is open: it would otherwise stay in the tab order behind a modal dialog. */
export function setLauncherHidden(button: HTMLButtonElement, hidden: boolean): void {
  button.hidden = hidden;
}

export function lockBodyScroll(): void {
  const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
  savedScrollY = window.scrollY;
  savedBodyOverflow = document.body.style.overflow;
  savedBodyPaddingRight = document.body.style.paddingRight;
  savedHtmlOverflow = document.documentElement.style.overflow;

  document.documentElement.style.overflow = "hidden";
  document.body.style.overflow = "hidden";
  if (scrollbarWidth > 0) {
    const currentPaddingRight = parseFloat(getComputedStyle(document.body).paddingRight) || 0;
    document.body.style.paddingRight = `${currentPaddingRight + scrollbarWidth}px`;
  }
}

/** Always called on every close path (X, Escape, API, CLOSE_REQUEST, teardown) — section 9. */
export function unlockBodyScroll(): void {
  document.documentElement.style.overflow = savedHtmlOverflow;
  document.body.style.overflow = savedBodyOverflow;
  document.body.style.paddingRight = savedBodyPaddingRight;
  // overflow:hidden never moved scrollY, but a defensive restore costs
  // nothing and protects against a host page that itself reacted to the
  // locked state by scrolling (e.g. its own sticky-header logic).
  window.scrollTo(0, savedScrollY);
}

const FOCUSABLE_SELECTOR = 'button, [href], input, select, textarea, iframe, [tabindex]:not([tabindex="-1"])';

/** Section 12: simple Tab/Shift+Tab trap within the modal subtree — the iframe itself counts as one focusable stop, matching normal tab order into its own document. */
export function trapFocus(modal: HTMLElement, event: KeyboardEvent): void {
  if (event.key !== "Tab") return;
  const focusable = Array.from(modal.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter((el) => !el.hasAttribute("hidden"));
  if (focusable.length === 0) return;
  const first = focusable[0]!;
  const last = focusable[focusable.length - 1]!;

  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}
